import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  ArrowLeft, Download, Pencil, CheckCircle2, Send, FilePlus2, History, Loader2, AlertTriangle, PenLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { practiceComplianceAPI as API, MANUAL_STATUS_META, downloadManualPdf } from '../lib/practiceComplianceAPI';

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-AU'); } catch { return iso; } };

export default function ManualDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editSection, setEditSection] = useState(null); // {key, title, body}
  const [approveOpen, setApproveOpen] = useState(false);
  const [approval, setApproval] = useState({
    signature_name: '', effective_date: new Date().toISOString().split('T')[0], next_review_date: '',
  });
  const [viewVersion, setViewVersion] = useState(null); // explicit version being viewed

  const load = useCallback(async () => {
    try { const res = await API.getManual(id); setData(res.data); setViewVersion(null); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to load manual'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  const { manual, versions, acknowledgements, can_manage } = data;
  const version = viewVersion || data.current_version;
  const isWorking = version && ['draft', 'in_review'].includes(version.status) && !version.immutable;
  const isEffective = version?.status === 'approved_effective';
  const meta = MANUAL_STATUS_META[version?.status] || {};

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const saveSection = async () => {
    setBusy(true);
    try {
      await API.editSection(id, version.id, editSection.key, { title: editSection.title, body: editSection.body });
      toast.success('Section saved');
      setEditSection(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save section'); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!approval.signature_name.trim()) { toast.error('Type your name as the electronic signature'); return; }
    setBusy(true);
    try {
      await API.approve(id, version.id, approval);
      toast.success(`Approved and effective — v${version.version} snapshot is now immutable`);
      setApproveOpen(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setBusy(false); }
  };

  const openVersion = async (vid) => {
    if (vid === version?.id) return;
    try { const res = await API.getVersion(id, vid); setViewVersion(res.data); }
    catch { toast.error('Failed to load version'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="manual-detail-page">
      <Button variant="ghost" onClick={() => navigate('/practice-manuals')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Practice Compliance
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{manual.name}</h1>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <span>Version {version?.version} ·</span>
            <Badge className={meta.color} data-testid="manual-status-badge">{meta.label || version?.status}</Badge>
            {manual.next_review_date && <span>Next review: {manual.next_review_date}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="manual-pdf-btn"
            onClick={() => downloadManualPdf(id, version?.id, `${manual.code.replace(' ', '_')}_v${version?.version}`).catch(() => toast.error('PDF failed'))}>
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
          {can_manage && version?.status === 'draft' && (
            <Button size="sm" variant="outline" disabled={busy} data-testid="manual-submit-review"
              onClick={() => act(() => API.submitReview(id, version.id), 'Submitted for review')}>
              <Send className="w-4 h-4 mr-1" /> Submit for review
            </Button>
          )}
          {can_manage && isWorking && (
            <Button size="sm" disabled={busy} onClick={() => setApproveOpen(true)} data-testid="manual-approve-btn">
              <PenLine className="w-4 h-4 mr-1" /> Approve &amp; make effective
            </Button>
          )}
          {can_manage && !isWorking && !versions.some((v) => ['draft', 'in_review'].includes(v.status)) && (
            <Button size="sm" variant="outline" disabled={busy} data-testid="manual-new-draft"
              onClick={() => act(() => API.newDraft(id, { change_note: 'Revision draft' }), 'New draft version created')}>
              <FilePlus2 className="w-4 h-4 mr-1" /> New draft version
            </Button>
          )}
          {isEffective && !data.my_acknowledged && (
            <Button size="sm" disabled={busy} data-testid="manual-acknowledge-btn"
              onClick={() => act(() => API.acknowledge(id), `You acknowledged ${manual.code} v${version.version}`)}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Acknowledge this version
            </Button>
          )}
          {!isEffective && data.effective_version_id && !data.my_acknowledged && (
            <Button size="sm" variant="outline" disabled={busy} data-testid="manual-acknowledge-effective-btn"
              onClick={() => act(() => API.acknowledge(id), `You acknowledged the effective version of ${manual.code}`)}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Acknowledge effective version
            </Button>
          )}
        </div>
      </div>

      {['draft', 'in_review'].includes(version?.status) && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="manual-draft-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><strong>DRAFT — pending principal approval.</strong> This document is not certified or implemented. A draft policy is not evidence of implementation.</span>
        </div>
      )}
      {manual.conditional && (
        <div className="flex items-start gap-2 text-sm text-orange-800 bg-orange-50 border border-orange-300 rounded-lg p-3" data-testid="manual-conditional-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><strong>Conditional applicability (ASQM 1).</strong> Applies only if the practice performs in-scope assurance engagements and has enabled ASQM 1 in the Practice Profile with attestation.</span>
        </div>
      )}
      {isEffective && data.my_acknowledged && (
        <p className="text-sm text-green-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> You acknowledged this version.</p>
      )}

      {/* Sections */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Sections</CardTitle>
          {isWorking && can_manage && <CardDescription className="text-xs">Draft version — click a section to expand, then Edit.</CardDescription>}
          {!isWorking && <CardDescription className="text-xs">This version is {version?.immutable ? 'an immutable approved snapshot' : 'read-only'}.</CardDescription>}
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="w-full">
            {(version?.sections || []).map((s, i) => (
              <AccordionItem key={s.key} value={s.key} data-testid={`manual-section-${s.key}`}>
                <AccordionTrigger className="text-sm font-medium text-left">
                  {s.title.startsWith('Appendix') ? s.title : `${i + 1}. ${s.title}`}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-foreground/85 whitespace-pre-wrap">{s.body}</p>
                  {isWorking && can_manage && (
                    <Button size="sm" variant="outline" className="mt-2" data-testid={`manual-edit-${s.key}`}
                      onClick={() => setEditSection({ ...s })}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit section
                    </Button>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Version history */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Version history</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {versions.map((v) => (
            <div key={v.id} className={`flex flex-wrap items-center justify-between gap-2 text-sm border rounded-lg p-2.5 ${v.id === version?.id ? 'border-primary/50 bg-primary/5' : ''}`}>
              <span className="flex items-center gap-2">
                <span className="font-medium">v{v.version}</span>
                <Badge className={(MANUAL_STATUS_META[v.status] || {}).color}>{(MANUAL_STATUS_META[v.status] || {}).label || v.status}</Badge>
                {v.approved_at && <span className="text-xs text-muted-foreground">Approved by {v.approved_by_name} · {fmt(v.approved_at)} · effective {v.effective_date}</span>}
                {v.change_note && !v.approved_at && <span className="text-xs text-muted-foreground">{v.change_note}</span>}
              </span>
              <span className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openVersion(v.id)} data-testid={`manual-view-v-${v.version}`}>View</Button>
                <Button size="sm" variant="ghost" onClick={() => downloadManualPdf(id, v.id, `${manual.code.replace(' ', '_')}_v${v.version}`).catch(() => toast.error('PDF failed'))}><Download className="w-4 h-4" /></Button>
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Acknowledgements for effective version */}
      {acknowledgements?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Acknowledgements (effective version)</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            {acknowledgements.map((a) => (
              <p key={a.id} className="text-muted-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 inline text-green-600 mr-1" />
                {a.user_name} acknowledged v{a.version} — {fmt(a.acknowledged_at)}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Edit section dialog */}
      <Dialog open={!!editSection} onOpenChange={(v) => !v && setEditSection(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit section</DialogTitle></DialogHeader>
          {editSection && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Title</Label>
                <Input value={editSection.title} onChange={(e) => setEditSection((x) => ({ ...x, title: e.target.value }))} /></div>
              <div className="space-y-1"><Label className="text-xs">Content</Label>
                <Textarea rows={10} value={editSection.body} onChange={(e) => setEditSection((x) => ({ ...x, body: e.target.value }))} data-testid="manual-section-editor" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditSection(null)}>Cancel</Button>
                <Button onClick={saveSection} disabled={busy} data-testid="manual-section-save">Save section</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Principal approval — {manual.code} v{version?.version}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Approving creates an immutable snapshot and marks this version effective. Staff will be required to acknowledge it. Future edits require a new draft version.
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Electronic signature — type your full name *</Label>
              <Input value={approval.signature_name} onChange={(e) => setApproval((x) => ({ ...x, signature_name: e.target.value }))} data-testid="manual-approve-signature" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Effective date *</Label>
                <Input type="date" value={approval.effective_date} onChange={(e) => setApproval((x) => ({ ...x, effective_date: e.target.value }))} data-testid="manual-approve-effective" /></div>
              <div className="space-y-1"><Label className="text-xs">Next review (default +1 yr)</Label>
                <Input type="date" value={approval.next_review_date} onChange={(e) => setApproval((x) => ({ ...x, next_review_date: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setApproveOpen(false)}>Cancel</Button>
              <Button onClick={approve} disabled={busy} data-testid="manual-approve-confirm">
                <PenLine className="w-4 h-4 mr-1" /> Sign &amp; approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
